import _ from "lodash";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Input, Label, Table, Row, Col, Button } from "reactstrap";
import { api, helpers, constants } from "../utils";
import { ItemDropdownMenu, ServiceThreadSpinner, SortHeader } from "./";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function VendorInventory({ vendorId, vendorName }) {
  const [data, setData] = useState([]);
  const [includeNoActivityItems, setIncludeNoActivityItems] = useState(false);
  const [secondarySortController, setSecondarySortController] = useState({
    sortDirection: "asc",
    sortField: "",
  })
  const [sites, setSites] = useState([]);
  const [sortController, setSortController] = useState({
    sortDirection: "asc",
    sortField: "",
  });
  const [working, setWorking] = useState(false);

  useEffect(refreshData, [vendorId, includeNoActivityItems, sortController]);

  const exportExcel = () => {
    let payload = {
      VendorId: vendorId,
      includeNoActivityItems: includeNoActivityItems,
      sortDirection: sortController.sortDirection,
      sortField: sortController.sortField,
    };
    api
      .postGetBlob(
        `Purchasing/ExportToExcelVendorInventory`, payload
      )
      .then((r) => {
        if (r.data.success === false) {
          if (_.isString(r?.data?.message)) {
            toast.error(r?.data?.message);
          }
          return;
        }
        if (r.data) {
          const XLSX_FILENAME = `CurrentInventory.xlsx`;
          helpers.downloadExport(
            r.data,
            XLSX_FILENAME,
            constants.MIME_TYPES.xlsx
          );
        } else {
          toast.error(r.message || "Error downloading report");
        }
      })
      .catch(api.catchHandler);
  };

  const onSortChange = (fieldName, Direction) => {
    setSortController({
      sortDirection: Direction,
      sortField: fieldName,
    });
  };

  const pageSpecificSorting = (state) => {
    const { sortField, sortDirection } = state;
    const newdata = _.orderBy(
      data,
      [sortField],
      [sortDirection]
    );
    
    setData(newdata);
    setSecondarySortController({sortDirection: sortDirection, sortField: sortField});
};

  function refreshData() {
    setWorking(true);
    let payload = {
      VendorId: vendorId,
      includeNoActivityItems: includeNoActivityItems,
      sortDirection: sortController.sortDirection,
      sortField: sortController.sortField,
    };
    api
      .post(`Purchasing/GetVendorInventory`, payload)
      .then((r) => {
        if (r.data.success) {
          setData(r.data.inventory);
          setSites(r.data.sites);
        } else {
          toast.error("Error getting Vendor Inventory Information");
          console.error(r.data.message);
        }
      })
      .catch(api.catchHandler)
      .finally(() => setWorking(false));
  }

  return (
    <>
      <div>
        <span className="h2 me-2" children="Current Inventory" />{" "}
        <span className="text-muted">Vendor {vendorId}</span>
        <span className="my-2 ms-1 float-end">
          <Input
            type="checkbox"
            className="my-2"
            checked={includeNoActivityItems}
            onChange={(e) => setIncludeNoActivityItems(e.target.checked)}
          />
          <Label className="pt-1 ms-3">
            Include Items With No Activity in Past 12 Months?
          </Label>
        </span>
      </div>
      <hr />
      <ServiceThreadSpinner on={working}>
        {data && data.length ? (
          <>
            <Row className={"mb-2"}>
              <Col
                xs={{ offset: 6, size: 6 }}
                className="transparent-background"
              >
                <Button
                  title="Download as .xlsx"
                  className="shredButtonExcel float-end ms-2"
                  onClick={exportExcel}
                  disabled={working}
                >
                  <FontAwesomeIcon icon="file-excel" size="lg" />
                </Button>
              </Col>
            </Row>
            <Table size="sm" className="data-table">
              <thead className="table-header">
                <tr>
                  <th>
                    <SortHeader
                      displayName={"Vendor Item"}
                      field="VendorItemDescription"
                      sortDirection={sortController.sortDirection}
                      sorted={
                        sortController.sortField === "VendorItemDescription"
                      }
                      callback={onSortChange}
                    />
                  </th>

                  <th>
                    <SortHeader
                      displayName={"Item"}
                      field="ItemDescription"
                      sortDirection={sortController.sortDirection}
                      sorted={sortController.sortField === "ItemDescription"}
                      callback={onSortChange}
                    />
                  </th>

                  <th>
                    <SortHeader
                      displayName={
                        <span>
                          Order
                          <br />
                          Point
                          <br />
                          Qty
                        </span>
                      }
                      field="OrderPoint"
                      sortDirection={sortController.sortDirection}
                      sorted={sortController.sortField === "OrderPoint"}
                      callback={onSortChange}
                    />
                  </th>

                  <th>Units</th>
                  <th>
                    <SortHeader
                      displayName={
                        <span>
                          12 Month
                          <br />
                          Quantity
                        </span>
                      }
                      field="YearQty"
                      sortDirection={sortController.sortDirection}
                      sorted={sortController.sortField === "YearQty"}
                      callback={onSortChange}
                    />
                  </th>

                  <th>
                    <SortHeader
                      displayName={
                        <span>
                          6 Month
                          <br />
                          Quantity
                        </span>
                      }
                      field="HalfYearQty"
                      sortDirection={sortController.sortDirection}
                      sorted={sortController.sortField === "HalfYearQty"}
                      callback={onSortChange}
                    />
                  </th>

                  <th>
                    <SortHeader
                      displayName={
                        <span>
                          3 Month
                          <br />
                          Quantity
                        </span>
                      }
                      field="QuarterYearQty"
                      sortDirection={sortController.sortDirection}
                      sorted={sortController.sortField === "QuarterYearQty"}
                      callback={onSortChange}
                    />
                  </th>

                  <th>
                    <SortHeader
                      displayName={
                        <span>
                          Current
                          <br />
                          Cost
                        </span>
                      }
                      field="UnitCost"
                      sortDirection={sortController.sortDirection}
                      sorted={sortController.sortField === "UnitCost"}
                      callback={onSortChange}
                    />
                  </th>

                  {_.map(sites, (site, idx) => {
                    return _.sumBy(
                      data,
                      (datum) => {
                        if (datum[`site${idx + 1}Qty`] >= 0) {
                          return datum[`site${idx + 1}Qty`];
                        }
                        return 0;
                      }
                    ) > 0 ? (
                      <PageSpecificSortHeader
                      displayName={<span>
                             {site.siteId}
                             <br />
                             Avail
                             <br />
                             Qty
                           </span>}
                      key={idx}
                      field={`site${idx + 1}Qty`}
                      sortDirection={secondarySortController.sortDirection}
                      sorted={secondarySortController.sortField === `site${idx + 1}Qty`}
                      callback={pageSpecificSorting}
                      state={{
                        sortField: `site${idx + 1}Qty`,
                        sortDirection: secondarySortController.sortDirection === "asc"
                          ? "desc"
                          : "asc"
                      }}
                      masterKey={`${site.siteId}_th`}
                      />
                    ) : null;
                  })}
                </tr>
              </thead>
              <tbody>
                {_.map(data, (datum, i) => {
                  return (
                    <tr
                      className={`tr-${i % 2 === 0 ? "primary" : "secondary"}`}
                      key={`${datum.vendorItem}_${i}`}
                    >
                      <td>{datum.vendorItemWithDescription}</td>
                      <ItemDropdownMenu
                        itemNumber={datum.itemNumber}
                        itemDescription={datum.itemWithDescription}
                        attachmentCount={datum.itemAttachmentCount}
                      />
                      <td>{datum.orderPoint.toLocaleString()}</td>
                      <td>{datum.unitOfMeasure}</td>
                      <td>{datum.yearQty.toLocaleString()}</td>
                      <td>{datum.halfYearQty.toLocaleString()}</td>
                      <td>{datum.quarterYearQty.toLocaleString()}</td>
                      <td>{helpers.formatCurrency(datum.unitCost)}</td>
                      {_.map(sites, (site, idx) => {
                        return _.sumBy(
                          data, (datum) => {
                            if (datum[`site${idx + 1}Qty`] >= 0) {
                              return datum[`site${idx + 1}Qty`];
                            }
                            return 0;
                          }
                        ) > 0 ? (
                          <td key={`${site.siteId}_td`}>
                            {datum[`site${idx + 1}Qty`].toLocaleString()}
                          </td>
                        ) : null;
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="table-footer">
                <tr>
                  <td colSpan={4} />
                  <td className="fw-bold">
                    {Math.round(_.sumBy(data, (x) => x.yearQty)).toLocaleString()}
                  </td>
                  <td className="fw-bold">
                    {_.sumBy(data, (x) => x.halfYearQty).toLocaleString()}
                  </td>
                  <td className="fw-bold">
                    {_.sumBy(data, (x) => x.quarterYearQty).toLocaleString()}
                  </td>
                  <td colSpan={1 + sites.length} />
                </tr>
              </tfoot>
            </Table>
          </>
        ) : (
          <div className="h5">No Inventory Data found</div>
        )}
      </ServiceThreadSpinner>
    </>
  );
}

function PageSpecificSortHeader(props) {
  const [direction, setDirection] = useState(props.sortDirection || "asc");
  const callBackDir = direction === "desc" ? "asc" : "desc";

  useEffect(() => {
    setDirection(props.sortDirection || "asc");
  }, [props.sortDirection]);

  return (
    <th>
       <span
        key={props.masterKey}
        className="sort-handle"
        onClick={() =>
          props.state
            ? props.callback(props.state)
            : props.callback(props.field, callBackDir)
        }
      >
        {props.displayName}
        {props.sorted && direction === "asc" && (
          <FontAwesomeIcon icon="angle-up" className="ms-1" />
        )}
        {props.sorted && direction === "desc" && (
          <FontAwesomeIcon icon="angle-down" className="ms-1" />
        )}
      </span>
    </th>
  );
}
